/**
 * Breadcrumb scroll fades (AAS-4671).
 *
 * Below 1025px (AAS-4874; originally below 750px) the breadcrumb shows the
 * full hierarchy in one horizontally scrollable strip. This writes `data-scroll` on each .breadcrumb__list so the
 * CSS can fade whichever edge still has content behind it:
 *
 *   (absent)  everything fits — no fade
 *   "end"     scrolled fully left, more to the right
 *   "start"   scrolled fully right, more to the left
 *   "both"    mid-scroll, content runs off both edges
 *
 * Scrolling itself is native (overflow-x: auto), so touch drag and momentum
 * come for free and keyboard/AT scrolling keeps working — no pointer handlers.
 *
 * Edge detection mirrors assets/activities.js: a 2px tolerance because
 * fractional scroll positions never land exactly on the bounds, and the RTL
 * branch because scrollLeft runs negative in RTL.
 */
(() => {
  const TOLERANCE = 2;

  const update = (list) => {
    const { scrollLeft, scrollWidth, clientWidth } = list;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= TOLERANCE) {
      list.removeAttribute('data-scroll');
      return;
    }

    const isRtl = getComputedStyle(list).direction === 'rtl';
    const atStart = isRtl ? Math.abs(scrollLeft) >= maxScroll - TOLERANCE : scrollLeft <= TOLERANCE;
    const atEnd = isRtl ? scrollLeft >= -TOLERANCE : scrollLeft >= maxScroll - TOLERANCE;

    // Name the edge that still has content behind it, not the edge we sit at.
    let state = 'both';
    if (atStart) state = 'end';
    else if (atEnd) state = 'start';
    list.setAttribute('data-scroll', state);
  };

  const bind = (list) => {
    if (list.dataset.scrollFadeBound) return;
    list.dataset.scrollFadeBound = '1';

    let frame = 0;
    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        update(list);
      });
    };

    list.addEventListener('scroll', schedule, { passive: true });

    // Content and available width both change without a scroll event — a
    // breadcrumb re-render (facets swap the grid, the PDP swaps trails) or a
    // rotate/resize. ResizeObserver covers both when available.
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(schedule);
      observer.observe(list);
      Array.from(list.children).forEach((child) => observer.observe(child));
    } else {
      window.addEventListener('resize', schedule);
    }

    update(list);
  };

  const bindAll = () => document.querySelectorAll('.breadcrumb__list').forEach(bind);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }

  // Web fonts change the trail's width after first paint, and the theme editor
  // re-renders sections without a page load.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(bindAll);
  document.addEventListener('shopify:section:load', bindAll);
})();
